import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { LoadingState, Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';
import { MyPayslips } from '@/features/hr/PayslipView';
import { MyMediaField } from './MyMediaField';
import { SignatureField } from './SignatureField';

interface Profile {
  id: number;
  username: string;
  fullName: string;
  title: string | null;
  phone: string | null;
  email: string | null;
  hasSignature: boolean;
}

/** CR-007: NV tự sửa số điện thoại + chữ ký in trên báo giá (họ tên, chức danh do quản trị sửa ở Nhân viên & phân quyền). */
export function MyProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me', 'profile'], queryFn: () => api.get<Profile>('/me/profile'), enabled: open });
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data) setPhone(q.data.phone ?? '');
  }, [q.data]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/me/profile', { phone });
      await qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      toast.success('Đã lưu số điện thoại');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Không lưu được');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Hồ sơ của tôi"
      description="Số điện thoại, chữ ký, hình ảnh và giọng nói, phiếu lương"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
          <Button onClick={() => void save()} disabled={busy || !q.data || phone === (q.data.phone ?? '')}>{busy && <Spinner />} Lưu số điện thoại</Button>
        </>
      }
    >
      {!q.data ? (
        <LoadingState />
      ) : (
        <div className="space-y-3">
          <div className="text-sm">
            <div className="font-medium">{q.data.fullName}</div>
            <div className="text-muted-foreground">{[q.data.title, q.data.email].filter(Boolean).join(' · ') || q.data.username}</div>
          </div>
          <div>
            <Label>Số điện thoại</Label>
            <Input value={phone} maxLength={30} inputMode="tel" onChange={(e) => setPhone(e.target.value)} placeholder="VD 0909 123 456" data-testid="my-phone" />
          </div>
          <SignatureField base="/me" hasSignature={q.data.hasSignature} onChanged={() => void qc.invalidateQueries({ queryKey: ['me', 'profile'] })} />
          {/* DEC-517: ảnh gương mặt, dáng người, giọng nói để vào vai trong phim bán hàng */}
          <MyMediaField fullName={q.data.fullName} />
          {/* DEC-626: CCCD, tài khoản nhận lương, học vấn, bằng cấp → NV tự nhập ở Cổng nhân viên */}
          <p className="text-sm">
            CCCD, tài khoản nhận lương, học vấn, bằng cấp:{' '}
            <Link to="/cong-nhan-vien?tab=thong-tin" className="text-primary underline" onClick={onClose} data-testid="my-private-link">
              mở Thông tin của tôi
            </Link>
          </p>
          {/* P21 B6: phiếu lương của chính mình, chỉ kỳ đã duyệt */}
          <MyPayslips />
        </div>
      )}
    </Dialog>
  );
}
